// Which extensions this line rings.
//
// Kevin, 2026-08-10: "All 5 extensions should ring. This involves a way to manage extensions
// assigned to a line" — and then, when only the API existed, "I don't see where to assign
// the extensions."
//
// One extension is the ordinary case and reads as it always did. Several means the number
// rings them all, and the build turns that into a ring group on the PBX. The ring group is
// never shown: an operator manages a line and its phones, not a routing object.

import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import ActionMenu from '../shared/ActionMenu.jsx';
import { RevealSecretDialog, useRevealSecret } from '../shared/RevealSecretDialog.jsx';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api.js';
import AddButton from '../shared/AddButton.jsx';
import FormModal from '../shared/FormModal.jsx';

export default function LineExtensions({ line, canEdit, onChooseDevice }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  // The one reveal path in the product: an acknowledgement per reveal, audited by surface,
  // and the value held in component state so it cannot be refetched in the background or
  // re-served after the operator hides it.
  const reveal = useRevealSecret('line-extensions');
  const [adding, setAdding] = useState(false);
  const [pick, setPick] = useState('');
  // The number typed when creating one rather than picking an existing extension.
  // Which row is being renamed, and to what. One at a time: a per-extension override, not a
  // bulk edit.
  const [naming, setNaming] = useState(null);
  const [nameDraft, setNameDraft] = useState('');

  const q = useQuery({
    queryKey: ['line', String(line.id), 'extensions'],
    queryFn: () => api.get(`/lines/${line.id}/extensions`),
  });

  // The customer's other extensions, to offer as candidates.
  const pool = useQuery({
    queryKey: ['subscriber', String(line.subscriberId), 'sip-accounts'],
    queryFn: () => api.get(`/sip-accounts?subscriberId=${line.subscriberId}`),
    enabled: Boolean(line.subscriberId) && adding,
  });

  // The extension's own name, which beats the line's when a device is configured (0052).
  // display_name_override, never display_name — that column records what was last pushed.
  const rename = useMutation({
    mutationFn: ({ id, value }) => api.patch(`/sip-accounts/${id}`, { display_name_override: value }),
    onSuccess: () => {
      setNaming(null); setNameDraft('');
      qc.invalidateQueries({ queryKey: ['line', String(line.id)] });
    },
  });

  const save = useMutation({
    mutationFn: (ids) => api.put(`/lines/${line.id}/extensions`, { sip_account_ids: ids }),
    onSuccess: () => {
      setAdding(false); setPick('');
      qc.invalidateQueries({ queryKey: ['line', String(line.id)] });
    },
  });

  // CREATE ONE HERE, RATHER THAN LEAVING THE PAGE FOR IT.
  //
  // "Add an extension" offered existing extensions only, so building Lyons Fire's five meant
  // going to the customer's Extensions section, creating one, coming back, attaching it, and
  // repeating — ten round trips for one line (Kevin, 2026-08-12: "how do I add an extension
  const rings = q.data?.extensions || [];
  const ids = rings.map((e) => e.sipAccountId);

  // that doesn't exist?"). The create and the attach are one action because they are one
  // intention; a created-but-unattached extension is the orphan state this page is meant to
  // stop producing.
  const create = useMutation({
    mutationFn: (username) => api.post(`/subscribers/${line.subscriberId}/sip-accounts`, { username }),
    onSuccess: (acct) => {
      qc.invalidateQueries({ queryKey: ['subscriber', String(line.subscriberId), 'sip-accounts'] });
      // Attach it in the same breath. If THIS fails the extension still exists and the
      // operator can pick it from the list — a partial that leaves something usable.
      save.mutate([...ids, acct.id]);
    },
  });

  // WHAT IS ACTUALLY FREE.
  //
  // This used to filter on `!ids.includes(a.id)` — this line's own ringing set and nothing
  // else — under a caption promising "only ones not already ringing for another number". On
  // line 136 it offered 13038232404 and 13038235857, both answering calls on lines 120 and
  // 121 (Kevin, 2026-08-13). The caption was not a stale comment; it described what the
  // operator was entitled to assume, and picking one would have taken a working number off
  // the phone that answers it.
  //
  // `ringsFor` comes from the server because the answer depends on the fallback: a line with
  // no explicit ringing list still rings its own account, so there is nothing in
  // `line_extensions` to see. The same function backs the server's refusal, so the picker and
  // the guard cannot drift apart.
  const pooled = (() => {
    const all = pool.data?.accounts || pool.data?.sipAccounts || pool.data || [];
    return (Array.isArray(all) ? all : []).filter((a) => !ids.includes(a.id));
  })();
  const candidates = pooled.filter((a) => !a.ringsFor);
  const takenCount = pooled.length - candidates.length;

  // WHAT THE NEXT ONE IS CALLED — decided by the SERVER, not guessed here.
  //
  // Kevin, 2026-08-26: "it should be fixed to match the DID... Adding an extension just adds the
  // predefined extension number. if an extension gets deleted and leaves a hole... the next
  // extension should be 3 to fill that hole."
  //
  // This used to be computed in this file and offered as a PLACEHOLDER on an editable field, so the
  // only thing standing between the fleet's convention and a hand-typed extension was an operator
  // not overtyping a suggestion. It also only knew about THIS line's extensions, so it could suggest
  // a username already taken elsewhere in the org and fail on create. `lib/extensionNumbering.js`
  // now owns the rule and the server applies it.
  const closeAdd = () => {
    setAdding(false); setPick(''); save.reset(); create.reset();
  };

  const nextExt = q.data?.next_extension || null;
  const suggestion = nextExt?.username || '';

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2
            className="text-base font-semibold text-gray-900"
            title="The extensions this number rings, and the device each one registers on. The first is the primary — hunt strategies ring in this order."
          >
            Extensions
          </h2>
        </div>
        {canEdit && !adding && (
          <div className="flex flex-shrink-0 items-center gap-2">
            {/* The card-level device button is gone: binding a device is per extension and
                each row's kebab offers it. The capability moved rather than being deleted —
                that mistake has been made twice on this page. */}
          {/* NOT OFFERED WHEN THE PACKAGE HOLDS ONE. The server refuses the second extension
              on the tier gate, so the button was an invitation to a 403 — and it sat directly
              under a card saying this package includes one extension. */}
          {line.multiExtension !== false && (
          <AddButton onClick={() => { save.reset(); setAdding(true); }}>
            Add an extension
          </AddButton>
          )}
          </div>
        )}
      </div>

      {q.isLoading && <p className="mt-3 text-sm text-gray-500">Loading extensions…</p>}
      {q.error && (
        <p className="mt-3 text-sm text-red-700">
          Could not load them: {q.error.body?.error || q.error.message}
        </p>
      )}

      {!q.isLoading && !q.error && (
        <ul className="mt-4 divide-y divide-gray-100">
          {rings.map((e, i) => (
            <li key={e.sipAccountId} className="flex items-center justify-between gap-3 py-2.5">
              <div className="min-w-0">
                <div>
                  <span className="font-mono text-sm text-gray-900">{e.username}</span>
                  {/* The name this phone will be given: the override when there is one, the
                      line's name otherwise. Shown either way, so the operator can see what the
                      screen will read without opening anything. */}
                  <span className="ml-2 text-xs text-gray-500">
                    {e.displayNameOverride || line.label || line.subscriberName}
                    {e.displayNameOverride && (
                      <span className="ml-1 rounded-full bg-blue-100 px-1.5 py-0.5 text-[10px] text-blue-800">
                        own name
                      </span>
                    )}
                  </span>
                  {i === 0 && rings.length > 1 && (
                    <span className="ml-2 rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] text-gray-600">
                      first
                    </span>
                  )}
                </div>
                {/* THE DEVICE IS PER EXTENSION. A line ringing five devices has five answers,
                    and the old line-level card could only show one — so line 122 read "no
                    phone" while five extensions rang on a DP750 (Kevin, 2026-08-10).
                    "Device", not "handset": an ATA, a door phone and a softphone are none of
                    them handsets (Kevin, 2026-08-11). */}
                <div className="mt-0.5 text-xs">
                  {e.deviceMac ? (
                    <>
                      <Link to={`/devices/${e.deviceMac}`} className="text-blue-600 hover:underline">
                        {e.deviceName || e.deviceModel || e.deviceMac}
                      </Link>
                      {e.devicePort != null && <span className="text-gray-500"> · port {e.devicePort}</span>}
                      {/* A Calix has no config to be given — it pulls its own from the ONT
                          side, so "never" is the permanent and correct state, not a fault
                          (Kevin, 2026-08-17). */}
                      {!e.deviceSyncedAt && !e.deviceSelfProvisioning && (
                        <span className="ml-1 text-amber-700">· never given its config</span>
                      )}
                    </>
                  ) : (
                    <span className="text-gray-500">No device</span>
                  )}
                </div>
              </div>

              <div className="flex flex-shrink-0 items-center gap-1">
                {/* The passphrase, revealed in place. Nothing is fetched until the eye is
                    clicked and the acknowledgement given — the value is not in the row's
                    payload at all, so an operator with the page open is not holding a
                    credential they did not ask for. */}
                {canEdit && (
                  revealedFor(reveal, e) ? (
                    <>
                      <span className="select-all font-mono text-xs text-gray-900">
                        {revealedFor(reveal, e)}
                      </span>
                      <button
                        type="button"
                        onClick={reveal.hide}
                        aria-label={`Hide the passphrase for ${e.username}`}
                        title="Hide it"
                        className="rounded-md border border-gray-300 p-1.5 text-gray-500 hover:bg-gray-50 hover:text-gray-700"
                      >
                        <EyeOff />
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      onClick={() => reveal.request({ id: e.sipAccountId, username: e.username })}
                      aria-label={`Show the passphrase for ${e.username}`}
                      title="Show the SIP passphrase"
                      className="rounded-md border border-gray-300 p-1.5 text-gray-500 hover:bg-gray-50 hover:text-gray-700"
                    >
                      <Eye />
                    </button>
                  )
                )}
                {canEdit && (
                  <ActionMenu
                    items={[
                      // The device and port for THIS extension. The line-level picker it
                      // replaced could only bind one, which is why a five-extension line
                      // reported no device at all.
                      // THIS extension's device, not the line's. It used to call the
                      // line-level dialog, which bound the line's own account: the row showed
                      // nothing and the build put every extension on that one device.
                      {
                        label: e.deviceMac ? 'Change the device' : 'Assign a device',
                        onClick: () => onChooseDevice?.(e),
                      },
                      { label: 'Open the extension', onClick: () => navigate(`/extensions/${e.sipAccountId}`) },
                      ...(e.deviceMac
                        ? [{ label: 'Open the device', onClick: () => navigate(`/devices/${e.deviceMac}`) }]
                        : []),
                      {
                        label: e.displayNameOverride ? 'Rename this extension' : 'Give it its own name',
                        onClick: () => {
                          rename.reset();
                          setNameDraft(e.displayNameOverride || '');
                          setNaming(e);
                        },
                      },
                      {
                        label: 'Show the SIP passphrase',
                        onClick: () => reveal.request({ id: e.sipAccountId, username: e.username }),
                      },
                      // Never offered for the last one: a line that rings nothing is a number
                      // that goes nowhere while still looking provisioned. The server refuses
                      // it too, so this is the UI agreeing rather than the only guard.
                      ...(rings.length > 1
                        ? [{
                          label: 'Remove from this line',
                          danger: true,
                          onClick: () => save.mutate(ids.filter((id) => id !== e.sipAccountId)),
                        }]
                        : []),
                    ]}
                  />
                )}
              </div>
            </li>
          ))}
          {!rings.length && (
            <li className="py-3 text-sm text-amber-800">
              This line rings nothing, so calls to the number reach no phone.
            </li>
          )}
        </ul>
      )}

      {/* "ALSO RINGS THESE NUMBERS" MOVED OUT, to ExternalRingCard.jsx.
          Kevin, 2026-08-26: "Separate out the 'Also rings these numbers' and also make them a
          destination with their own card."
          It was a section in here, which made the extensions card the place you go to manage
          something that is not an extension. Both cards now sit at the same level under the
          destination, each showing part of what is inside it. */}

      {reveal.error && (
        <p className="mt-2 text-sm text-red-700">{reveal.error}</p>
      )}

      <RevealSecretDialog
        account={reveal.pending}
        onConfirm={reveal.confirm}
        onCancel={reveal.cancel}
        isPending={reveal.isPending}
        error={reveal.error}
      />

      {naming && (
        <div className="fixed inset-0 z-40 flex items-start justify-center overflow-y-auto bg-black/40 p-4 sm:p-8">
          <div className="w-full max-w-lg rounded-lg bg-white shadow-xl">
            <div className="border-b border-gray-200 px-5 py-4">
              <h2 className="text-base font-semibold text-gray-900">
                Name for extension {naming.username}
              </h2>
              <p className="mt-1 text-sm text-gray-600">
                What this phone shows on its screen, and what it presents to other extensions.
                Leave it blank to use the line&rsquo;s name.
              </p>
            </div>
            <div className="space-y-3 px-5 py-4">
              <input
                aria-label="Name for this extension"
                value={nameDraft}
                maxLength={15}
                placeholder={line.label || line.subscriberName || ''}
                onChange={(ev) => setNameDraft(ev.target.value)}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              />
              <p className="text-xs text-gray-500">
                {15 - nameDraft.length} characters left — a phone screen holds 15. Applied on
                the next build.
              </p>
              {rename.isError && (
                <p className="text-sm text-red-700">
                  {rename.error.body?.error || rename.error.message}
                </p>
              )}
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  disabled={rename.isPending}
                  onClick={() => rename.mutate({ id: naming.sipAccountId, value: nameDraft.trim() })}
                  className="rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                >
                  {rename.isPending ? 'Saving…' : 'Save'}
                </button>
                <button
                  type="button"
                  onClick={() => { setNaming(null); setNameDraft(''); rename.reset(); }}
                  className="rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {adding && (
        <FormModal
          title="Add an extension"
          subtitle={line.number ? `for ${line.number}` : null}
          onClose={closeAdd}
        >
        <div>
          <label htmlFor="line-ext-add" className="block text-xs font-medium uppercase tracking-wide text-gray-500">
            Extension
          </label>
          <select
            id="line-ext-add"
            value={pick}
            onChange={(e) => setPick(e.target.value)}
            className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
          >
            <option value="">
              {candidates.length ? 'Choose an extension' : 'No existing extension is free'}
            </option>
            {candidates.map((a) => (
              <option key={a.id} value={a.id}>
                {a.username}{a.displayName ? ` · ${a.displayName}` : ''}
              </option>
            ))}
            {/* LAST, and always offered. On a new line there is nothing to pick, and the
                empty select was a dead end that sent the operator to another page. */}
            <option value="__new">Create a new extension…</option>
          </select>

          {pick === '__new' && (
            <div className="mt-3">
              <div className="text-xs font-medium uppercase tracking-wide text-gray-500">
                New extension number
              </div>
              {/* NOT A FIELD. The number follows from the line's own number, so there is nothing to
                  decide and an input would only invite a mistake nobody can see afterwards. */}
              {suggestion ? (
                <>
                  <p className="mt-1 font-mono text-sm text-gray-900">{suggestion}</p>
                  <p className="mt-1 text-xs text-gray-500">
                    Extension {nextExt.index} for {line.number} — the next free number for this line.
                    Created under this customer and attached in one step.
                  </p>
                </>
              ) : (
                // A line with no number, or all nine in use. Both need the sentence, not a disabled
                // button with no explanation.
                <p className="mt-1 text-xs text-amber-800">
                  {nextExt?.why || 'There is no extension number to add for this line.'}
                </p>
              )}
            </div>
          )}
          <p className="mt-1 text-xs text-gray-500">
            Only this customer&rsquo;s extensions, and only ones not already ringing for
            another number.
          </p>
          {/* SAY WHAT WAS WITHHELD. An empty or short list with no reason reads as broken
              software; naming the count says the filter did its job. Which number holds each
              one is on that number's own page — this is a picker, not a report. */}
          {takenCount > 0 && (
            <p className="mt-1 text-xs text-gray-500">
              {takenCount === 1
                ? '1 more already rings for another number, so it is not offered.'
                : `${takenCount} more already ring for other numbers, so they are not offered.`}
            </p>
          )}

          {(save.isError || create.isError) && (
            <p className="mt-3 text-sm text-red-700">
              {(create.error || save.error)?.body?.error || (create.error || save.error)?.message}
            </p>
          )}

          <div className="mt-3 flex items-center gap-2">
            <button
              type="button"
              disabled={
                pick === '__new'
                  ? (!suggestion || create.isPending || save.isPending)
                  : (!pick || save.isPending)
              }
              onClick={() => {
                if (pick === '__new') create.mutate(suggestion);
                else save.mutate([...ids, Number(pick)]);
              }}
              className="rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {create.isPending ? 'Creating…' : save.isPending ? 'Saving…' : 'Add'}
            </button>
            <button
              type="button"
              onClick={closeAdd}
              className="rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <span className="text-xs text-gray-500">Saved to Verso; applied on the next build.</span>
          </div>
        </div>
        </FormModal>
      )}
    </div>
  );
}

// Which row the revealed value belongs to. One reveal at a time, deliberately: two
// passphrases on screen at once is two credentials nobody asked to still be looking at.
function revealedFor(reveal, e) {
  if (!reveal.revealed) return null;
  return reveal.revealed.account?.id === e.sipAccountId ? reveal.revealed.secret : null;
}

function Eye() {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4" aria-hidden="true">
      <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
      <path
        fillRule="evenodd"
        d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z"
        clipRule="evenodd"
      />
    </svg>
  );
}

function EyeOff() {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4" aria-hidden="true">
      <path
        fillRule="evenodd"
        d="M3.707 2.293a1 1 0 00-1.414 1.414l14 14a1 1 0 001.414-1.414l-1.473-1.473A10.014 10.014 0 0019.542 10C18.268 5.943 14.478 3 10 3a9.958 9.958 0 00-4.512 1.074l-1.78-1.781zm4.261 4.26l1.514 1.515a2.003 2.003 0 012.45 2.45l1.514 1.514a4 4 0 00-5.478-5.478z"
        clipRule="evenodd"
      />
      <path d="M12.454 16.697L9.75 13.992a4 4 0 01-3.742-3.741L2.335 6.578A9.98 9.98 0 00.458 10c1.274 4.057 5.065 7 9.542 7 .847 0 1.669-.105 2.454-.303z" />
    </svg>
  );
}
